/**
 * MaterialGalleryGrid — responsive grid of MaterialThumbnail cards.
 *
 * Layout mirrors ui/library/LibraryGrid.tsx so the gallery reads as an
 * extension of the existing library UI. Includes a sort control and an
 * empty state. Selection state is lifted to the parent modal.
 */

import { ArrowDownNarrowWide, Layers } from "lucide-react";
import type { CSSProperties } from "react";
import type { SceneMaterial } from "../../modeling/types.ts";
import {
  COLOR_BORDER_STRONG,
  COLOR_SURFACE,
  COLOR_TEXT_DIM,
  COLOR_TEXT_FAINT,
  COLOR_TEXT_SECONDARY,
  FONT_SIZE_BODY,
  FONT_SIZE_SMALL,
  RADIUS_CONTROL,
} from "../tokens.ts";
import { MaterialThumbnail } from "./MaterialThumbnail.tsx";

export type GallerySortOrder = "recent" | "alphabetical";

export interface GalleryItem {
  id: string;
  name: string;
  properties: SceneMaterial;
  linked?: boolean;
  userCount?: number;
  updatedAt?: number;
}

interface MaterialGalleryGridProps {
  items: GalleryItem[];
  selectedId: string | null;
  sortOrder: GallerySortOrder;
  onSortChange: (order: GallerySortOrder) => void;
  onSelect: (id: string) => void;
  emptyTitle: string;
  emptySubtitle: string;
}

const SORT_OPTIONS: { value: GallerySortOrder; label: string }[] = [
  { value: "recent", label: "Recently Modified" },
  { value: "alphabetical", label: "Alphabetical" },
];

const TOOLBAR: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  marginBottom: 12,
};

const SORT_SELECT: CSSProperties = {
  background: COLOR_SURFACE,
  color: COLOR_TEXT_SECONDARY,
  border: `1px solid ${COLOR_BORDER_STRONG}`,
  borderRadius: RADIUS_CONTROL,
  padding: "4px 8px",
  fontSize: FONT_SIZE_SMALL,
  cursor: "pointer",
  outline: "none",
};

const GRID: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
  gap: 10,
};

function sortItems(
  items: GalleryItem[],
  order: GallerySortOrder,
): GalleryItem[] {
  const copy = items.slice();
  if (order === "alphabetical") {
    copy.sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: "base" }),
    );
  } else {
    copy.sort((a, b) => (b.updatedAt ?? 0) - (a.updatedAt ?? 0));
  }
  return copy;
}

export function MaterialGalleryGrid({
  items,
  selectedId,
  sortOrder,
  onSortChange,
  onSelect,
  emptyTitle,
  emptySubtitle,
}: MaterialGalleryGridProps) {
  const sorted = sortItems(items, sortOrder);

  if (sorted.length === 0) {
    return (
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 10,
          color: COLOR_TEXT_FAINT,
          padding: 24,
        }}
      >
        <Layers size={40} style={{ opacity: 0.5 }} />
        <span style={{ fontSize: FONT_SIZE_BODY }}>{emptyTitle}</span>
        <span
          style={{
            fontSize: FONT_SIZE_SMALL,
            color: COLOR_TEXT_DIM,
            textAlign: "center",
          }}
        >
          {emptySubtitle}
        </span>
      </div>
    );
  }

  return (
    <div
      style={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        padding: 14,
      }}
    >
      <div style={TOOLBAR}>
        <ArrowDownNarrowWide
          size={14}
          style={{ color: COLOR_TEXT_DIM, flexShrink: 0 }}
        />
        <select
          value={sortOrder}
          onChange={(e) => onSortChange(e.target.value as GallerySortOrder)}
          style={SORT_SELECT}
        >
          {SORT_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>
      <div style={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
        <div style={GRID}>
          {sorted.map((item) => (
            <MaterialThumbnail
              key={item.id}
              name={item.name}
              properties={item.properties}
              selected={item.id === selectedId}
              linked={item.linked}
              userCount={item.userCount}
              onSelect={() => onSelect(item.id)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
